import { useState, useEffect, useRef } from 'react'
import { HttpTransportType, HubConnectionBuilder } from '@microsoft/signalr'
import { toastNotification } from './apiFunctions'
import { useStateContext } from "../contexts/ContextProvider"
import { TaskStatusType } from './enums'
import { useQueryClient } from '@tanstack/react-query'


function useEventListener({ eventName, handler, ref, dependencies = [], breakCondition = () => false }) {
    const savedHandler = useRef();

    useEffect(() => {
        savedHandler.current = handler;
    }, [handler]);

    useEffect(
        () => {
            let localRef = null;
            if (ref.current) localRef = ref.current;

            if (breakCondition(dependencies)) {
                return;
            }

            const isSupported = ref.current && ref.current.addEventListener;
            if (!isSupported) {
                return;
            }

            const eventListener = function (event) {
                savedHandler.current(event);
            };

            ref.current.addEventListener(eventName, eventListener);

            return () => {
                localRef.removeEventListener(eventName, eventListener);
            };
        },
        [eventName, ref, ...dependencies]
    );
};

const showNotification = (taskStatusType, hasUnhandledEvents, message, eventPageType) => {
    switch (taskStatusType) {
        case 2:
            toastNotification(`${message} started.`, 'informative');
            break;
        case 4:
            if (hasUnhandledEvents) {
                toastNotification(`${message} generated events. Please check the ${eventPageType} Events page.`, 'warning');
            } else {
                toastNotification(`${message} finished successfully.`, 'success');
            }
            break;
        case 5:
            toastNotification(`${message} failed.`, 'error');
            break;
        default:
            break;
    }
}

function useLiveUpdates() {
    const [connectionRef, setConnectionRef] = useState();
    const { user, setTaskStatus, setFinishedOnDemandInventoryTask } = useStateContext();
    const queryClient = useQueryClient();

    function createHubConnection() {
        const con = new HubConnectionBuilder()
            .withUrl(process.env.REACT_APP_KASEYA_STUB_API_URL + '/api/notifications', { skipNegotiation: false, transport: HttpTransportType.WebSockets })
            .withAutomaticReconnect()
            .build();
        setConnectionRef(con);
    }

    const updateInventoryOnDemandTask = (taskIdentifier, taskStatusType) => {
        if (taskStatusType === TaskStatusType.SUCCEEDED || taskStatusType === TaskStatusType.FAILED) {
            setFinishedOnDemandInventoryTask({ taskIdentifier: taskIdentifier, taskStatusType: taskStatusType });
        }
    }

    useEffect(() => {
        if (!connectionRef && (user?.tenantInfo.integrationId && user?.tenantInfo.integrationId.trim() !== '')) {
           createHubConnection();
        }
    }, [user]);

    useEffect(() => {
        const refetchInventoryEventsOnTaskSuccess = async (taskStatusType) => {
            if (taskStatusType === TaskStatusType.SUCCEEDED) {
                await queryClient.invalidateQueries(
                    {
                      queryKey: ['fetchInventoryEvents'],
                      exact: true,
                      refetchType: 'all',
                    },
                    { throwOnError: false, cancelRefetch: false },
                  )
                await queryClient.refetchQueries({ queryKey: ['fetchOrgs'] })
            }
        }
        const refetchAgentEventsOnTaskSuccess = async (taskStatusType) => {
            if (taskStatusType === TaskStatusType.SUCCEEDED) {
                await queryClient.invalidateQueries(
                    {
                      queryKey: ['fetchAgentEvents'],
                      exact: true,
                      refetchType: 'all',
                    },
                    { throwOnError: false, cancelRefetch: false },
                  )
            }
        }

        if (connectionRef && (user?.tenantInfo.integrationId && user?.tenantInfo.integrationId.trim() !== '')) {
            try {
                connectionRef
                    .start()
                    .then(() => {
                        connectionRef.on('InventoryTaskNotification', data => {
                            let message = "Inventory sync task"
                            let isRecurrentTask = false

                            if (data.payload.runType === 1) {
                                message = "Recurrent inventory sync task"
                                isRecurrentTask = true
                            }

                            updateInventoryOnDemandTask(data.payload.taskIdentifier, data.payload.taskStatusType)
                            showNotification(data.payload.taskStatusType, data.payload.generatedEvents > data.payload.handledEvents, message, 'Inventory');
                            setTaskStatus({ taskStatusType: data.payload.taskStatusType, typeOfTask: 'inventory', isRecurrentTask: isRecurrentTask});
                            refetchInventoryEventsOnTaskSuccess(data.payload.taskStatusType).catch(console.error);
                        })
                    })
                    .catch((err) => {
                        console.log(`Error: ${err}`);
                    })
                    .then(() => {
                        connectionRef.on('EndpointTaskNotification', data => {
                            let message = "Agent sync task"
                            let isRecurrentTask = false

                            if (data.payload.runType === 1) {
                                message = "Recurrent agent sync task"
                                isRecurrentTask = true
                            }

                            showNotification(data.payload.taskStatusType, data.payload.generatedEvents > data.payload.handledEvents, message, 'Agent');
                            setTaskStatus({ taskStatusType: data.payload.taskStatusType, typeOfTask: 'agent', isRecurrentTask: isRecurrentTask});
                            refetchAgentEventsOnTaskSuccess(data.payload.taskStatusType).catch(console.error);
                        })
                    })
                    .catch((err) => {
                        console.log(`Error: ${err}`);
                    });
            } catch (error) {
                console.log(`Error: ${error}`);
            };
        }

        return () => {
            if (connectionRef) {
                connectionRef.stop();
            }
        };
    }, [connectionRef]);
};

export {
    useEventListener,
    useLiveUpdates
}